import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "../../../assets/images/Home/logo.svg";
import { throttle, screenType, hasUTMParams, getUTMParamsStr } from '../utils.js';
import "./index.scss";
import { goLoginOrSignUp } from '@/utils/common.js';
import XBTIBanner from "../XBTIBanner";

const cnFeaturePaths = [
  "/cn/ad-management",
  "/cn/batch-builder",
  "/cn/creative-center",
];

function Header(props) {
  const { t } = useTranslation();
  let { i18n } = useTranslation();
  const navigate = useNavigate();
  const [type, setType] = useState("");

  const [isDropDown, setDropDown] = useState(false);
  const [isDesktopCompareOpen, setDesktopCompareOpen] = useState(false);
  const pathInfo = useLocation();
  const isComparisonPage = pathInfo.pathname.startsWith('/en/comparison/');
  const [isMobileCompareOpen, setMobileCompareOpen] = useState(isComparisonPage);
  const utmParamsStr = getUTMParamsStr();
  const pathList = useMemo(() => {
    const list = [
      { id: 1, name: "header.use", url: `/${i18n.language}/use` },
      { id: 2, name: "header.example", url: `/${i18n.language}/example` },
      { id: 3, name: "header.blog", url: hasUTMParams() ? `/${i18n.language}-blog?${utmParamsStr}` : `/${i18n.language}-blog` },
      { id: 5, name: "header.appsolution", url: `/${i18n.language}/appsolution` },
    ];

    if (i18n.language === 'cn') {
      list.push({ id: 4, name: "header.ecommerce", url: `/cn/ecommerce` });
    } else {
      list.push({
        id: 6,
        name: "header.compare",
        children: [
          {
            id: "compare-madgicx",
            name: "header.xmpVsMadgicx",
            url: "/en/comparison/xmp-vs-madgicx",
          },
          {
            id: "compare-smartly",
            name: "header.xmpVsSmartly",
            url: "/en/comparison/xmp-vs-smartly",
          },
        ],
      });
    }

    list.sort((a, b) => a.id - b.id);

    return list;
  }, [i18n.language, utmParamsStr]
  );
  const activeIndex = useMemo(
    () => pathList.findIndex((item) => (
      item.url === pathInfo.pathname
      || item.children?.some((child) => child.url === pathInfo.pathname)
      || (item.id === 1 && cnFeaturePaths.includes(pathInfo.pathname))
    )),
    [pathInfo.pathname, pathList]
  );

  const handleResize = useCallback(() => {
    const throttleFn = throttle(() => {
      setType(screenType());
    }, 300);
    throttleFn();
  }, [setType]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, [handleResize]);

  const toPath = (item) => () => {
    setDropDown(false);
    setDesktopCompareOpen(false);
    setMobileCompareOpen(false);
    navigate(item.url);
  };

  const toggleLanguage = useCallback((lang) => {
    const { pathname } = window.location;
    let omitLangPath = pathname.replace(/^\/\w+/, '');
    let naviateUrl = `${lang}${omitLangPath}`;
    if (lang === 'cn' && pathname.startsWith('/en/comparison/')) {
      naviateUrl = '/cn';
    } else if (lang === 'en' && cnFeaturePaths.includes(pathname)) {
      naviateUrl = '/en/use';
    }
    navigate(naviateUrl);
    i18n.changeLanguage(lang);
    setDropDown(false);
    setDesktopCompareOpen(false);
    setMobileCompareOpen(false);
    window.location.reload();
  }, [setDropDown, i18n, navigate])

  const handleLoginOrSignUp = () => {
    goLoginOrSignUp(i18n.language);
  }

  return (
    <>
      {i18n.language === 'cn' && <XBTIBanner />}
      <div className={`Header ${i18n.language}`}>

      <Logo className="logo" onClick={toPath({ url: `/${i18n.language}/` })} />
      <div className="path_wrapper">
        {pathList.map((item, index) => {
          if (item.children) {
            return (
              <div
                className={`path_item compare_menu font-black ${activeIndex === index ? 'active' : ''} ${isDesktopCompareOpen ? 'is-open' : ''}`}
                key={item.id}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setDesktopCompareOpen(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setDesktopCompareOpen(false);
                    event.currentTarget.querySelector('.compare_trigger')?.focus();
                  }
                }}
              >
                <button
                  type="button"
                  className="compare_trigger font-black"
                  aria-haspopup="menu"
                  aria-expanded={isDesktopCompareOpen}
                  onClick={() => setDesktopCompareOpen(true)}
                  onFocus={() => setDesktopCompareOpen(true)}
                >
                  {t(item.name)}
                  <span className="compare_chevron" aria-hidden="true" />
                </button>
                <div className="compare_submenu">
                  {item.children.map((child) => (
                    <a
                      className={`compare_submenu_item ${child.url === pathInfo.pathname ? 'is-active' : ''}`}
                      href={child.url}
                      key={child.id}
                      onClick={() => setDesktopCompareOpen(false)}
                    >
                      {t(child.name)}
                    </a>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <a
              className={`path_item font-black ${activeIndex === index ? 'active' : ''}`}
              href={item.url}
              key={item.id}
            >
              {t(item.name)}
            </a>
          );
        })}
      </div>

      <div className="app_wrapper">
        <button onClick={handleLoginOrSignUp} className="apply button_link">
          <span className='font-bold'>{t('header.apply')}</span>
        </button>
        <div className="lang font-black">
          <span className="font-black">{t("header.lang")}</span>
          <div className="dropDown">
            {i18n.language === "cn" && (
              <div
                onClick={() => toggleLanguage('en')}
              >
                EN
              </div>
            )}
            {i18n.language === "en" && (
              <div
                onClick={() => toggleLanguage('cn')}
              >
                CN
              </div>
            )}
          </div>
        </div>
        {type === "mobile" && (
          <button
            type="button"
            className="drop"
            aria-label="Toggle navigation"
            aria-expanded={isDropDown}
            onClick={() => {
              setDropDown(!isDropDown);
            }}
          >
            <div></div>
            <div></div>
            <div></div>
          </button>
        )}
      </div>
      {isDropDown && (
        <div className="drop_list">
          <div className="path_wrapper">
            {pathList.map((item) => {
              if (item.children) {
                return (
                  <div
                    className="path_item mobile_compare_menu font-bold"
                    key={item.id}
                  >
                    <button
                      type="button"
                      className="mobile_compare_trigger font-bold"
                      aria-expanded={isMobileCompareOpen}
                      onClick={() => setMobileCompareOpen((isOpen) => !isOpen)}
                    >
                      {t(item.name)}
                      <span className="compare_chevron" aria-hidden="true" />
                    </button>
                    {isMobileCompareOpen && (
                      <div className="mobile_compare_submenu">
                        {item.children.map((child) => (
                          <a
                            className={`mobile_compare_item ${child.url === pathInfo.pathname ? 'is-active' : ''}`}
                            href={child.url}
                            key={child.id}
                            onClick={() => setDropDown(false)}
                          >
                            {t(child.name)}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div
                  className={[
                    "path_item font-bold",
                  ].join(" ")}
                  key={item.id}
                >
                  <a href={item.url} rel="noreferrer">
                    {t(item.name)}
                  </a>
                </div>
              );
            })}
          </div>
          <div className="lang">
            <div
              className={i18n.language === "cn" ? "active" : ""}
              onClick={() => toggleLanguage('cn')}
            >
              简体中文
            </div>
            <div
              className={i18n.language === "en" ? "active" : ""}
              onClick={() => toggleLanguage('en')}
            >
              EN
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default Header;
